package com.tregz.tremenz.ui.component

import androidx.compose.animation.animateContentSize
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.minimumInteractiveComponentSize
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.tregz.tremenz.io.model.ModelData
import com.tregz.tremenz.ui.icon.IconKey
import com.tregz.tremenz.ui.icon.iconPainter
import com.tregz.tremenz.ui.label.LabelKey
import com.tregz.tremenz.ui.label.labelText
import com.tregz.tremenz.ui.media.MediaImage

@Composable
fun CardMedia(
    media: ModelData.Media,
    isPlaying: Boolean,
    modifier: Modifier = Modifier,
    isPreparing: Boolean = false,
    onPlayClick: () -> Unit,
    onInfoClick: (() -> Unit)? = null,
    onOpenLink: ((String) -> Unit)? = null
) {
    var isExpanded by rememberSaveable { mutableStateOf(false) }
    val scheme = MaterialTheme.colorScheme

    Card(
        modifier = modifier
            .fillMaxWidth()
            .padding(horizontal = 12.dp, vertical = 6.dp)
            .animateContentSize(),
        colors = CardDefaults.cardColors(
            containerColor = scheme.surfaceContainer
        ),
        shape = RoundedCornerShape(8.dp)
    ) {
        Column(modifier = Modifier.fillMaxWidth()) {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .clickable(onClick = onPlayClick)
                    .padding(8.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                // Cover Image
                MediaImage(
                    url = media.posterUrl,
                    contentDescription = null,
                    modifier = Modifier
                        .size(64.dp)
                        .clip(RoundedCornerShape(4.dp))
                        .background(scheme.surfaceVariant),
                    contentScale = ContentScale.Crop
                )

                Spacer(modifier = Modifier.width(12.dp))

                // Titles & Metadata
                Column(
                    modifier = Modifier.weight(1f)
                ) {
                    Text(
                        text = media.toString(),
                        color = if (isPlaying) scheme.primary else scheme.onSurface,
                        style = MaterialTheme.typography.titleMedium
                    )
                    if (media.year > 0) {
                        Text(
                            text = "${media.year}",
                            color = scheme.onSurfaceVariant,
                            style = MaterialTheme.typography.bodySmall
                        )
                    }
                }

                if (isPreparing) {
                    CircularProgressIndicator(
                        color = scheme.primary,
                        modifier = Modifier.size(24.dp),
                        strokeWidth = 2.5.dp
                    )
                }

                // Details Button
                IconButton(
                    onClick = {
                        isExpanded = !isExpanded
                        onInfoClick?.invoke()
                    }
                ) {
                    Icon(
                        painter = iconPainter(if (isExpanded) IconKey.COLLAPSE else IconKey.INFO),
                        contentDescription = labelText(if (isExpanded) LabelKey.MEDIA_COLLAPSE else LabelKey.MEDIA_DETAILS),
                        tint = if (isExpanded) scheme.primary else scheme.onSurfaceVariant
                    )
                }
            }

            if (isExpanded) {
                HorizontalDivider(
                    color = scheme.outlineVariant,
                    thickness = 0.5.dp,
                    modifier = Modifier.padding(horizontal = 8.dp)
                )
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = 12.dp, vertical = 8.dp)
                ) {
                    if (media is ModelData.Song) {
                        if (!media.featuring.isNullOrBlank()) {
                            Text(
                                text = "${labelText(LabelKey.MEDIA_FEATURING)}: ${media.featuring}",
                                style = MaterialTheme.typography.bodyMedium,
                                color = scheme.onSurfaceVariant,
                                modifier = Modifier.padding(bottom = 2.dp)
                            )
                        }
                        if (!media.mixedBy.isNullOrBlank()) {
                            Text(
                                text = "${labelText(LabelKey.MEDIA_MIXED_BY)}: ${media.mixedBy}",
                                style = MaterialTheme.typography.bodyMedium,
                                color = scheme.onSurfaceVariant,
                                modifier = Modifier.padding(bottom = 2.dp)
                            )
                        }
                        if (!media.genre.isNullOrBlank()) {
                            Text(
                                text = "${labelText(LabelKey.MEDIA_GENRE)}: ${media.genre}",
                                style = MaterialTheme.typography.bodyMedium,
                                color = scheme.onSurfaceVariant,
                                modifier = Modifier.padding(bottom = 2.dp)
                            )
                        }
                        Text(
                            text = "YouTube ID: ${media.id}",
                            style = MaterialTheme.typography.bodySmall,
                            color = scheme.outline,
                            modifier = Modifier.padding(top = 2.dp, bottom = 4.dp)
                        )
                    }

                    if (media.list.isNotEmpty()) {
                        Text(
                            text = "${labelText(LabelKey.MEDIA_TRACKLIST)} (${media.list.size})",
                            style = MaterialTheme.typography.titleSmall,
                            fontWeight = FontWeight.Bold,
                            color = scheme.primary,
                            modifier = Modifier.padding(bottom = 6.dp)
                        )
                        media.list.forEach { track ->
                            Row(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(vertical = 3.dp),
                                verticalAlignment = Alignment.Top
                            ) {
                                Text(
                                    text = if (track.id.endsWith(".")) track.id else "${track.id}. ",
                                    style = MaterialTheme.typography.bodyMedium,
                                    fontWeight = FontWeight.SemiBold,
                                    color = scheme.outline
                                )
                                Column(modifier = Modifier.weight(1f)) {
                                    Text(
                                        text = track.name,
                                        style = MaterialTheme.typography.bodyMedium,
                                        fontWeight = FontWeight.Medium,
                                        color = scheme.onSurface
                                    )
                                    if (track.author.isNotBlank()) {
                                        Text(
                                            text = track.author,
                                            style = MaterialTheme.typography.bodySmall,
                                            color = scheme.onSurfaceVariant
                                        )
                                    }
                                }
                            }
                        }
                    } else if (media !is ModelData.Song) {
                        Text(
                            text = labelText(LabelKey.MEDIA_NO_TRACKLIST),
                            style = MaterialTheme.typography.bodyMedium,
                            color = scheme.outline,
                            fontStyle = FontStyle.Italic,
                            modifier = Modifier.padding(vertical = 4.dp)
                        )
                    }

                    val audioUrl = (media as? ModelData.Audio)?.sono
                    if (!audioUrl.isNullOrBlank()) {
                        val platform = if (audioUrl.contains("soundcloud")) "SoundCloud" else "Mixcloud"
                        Text(
                            text = "${labelText(LabelKey.MEDIA_OPEN_ON)} $platform ↗",
                            style = MaterialTheme.typography.labelLarge,
                            fontWeight = FontWeight.SemiBold,
                            color = scheme.primary,
                            modifier = Modifier
                                .minimumInteractiveComponentSize()
                                .clickable { onOpenLink?.invoke(audioUrl) }
                                .padding(vertical = 4.dp)
                        )
                    }
                }
            }
        }
    }
}
